import React from 'react';
import {
  User,
  Mail,
  Phone,
  Shield,
  Bell,
  Pause,
  CheckCircle,
  AlertCircle,
  Info,
  Lock,
  Save,
  MessageSquare
} from 'lucide-react';

const UserProfileDocs: React.FC = () => {
  return (
    <div className="max-w-5xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-gradient-to-r from-green-500 to-brandColor p-3 rounded-lg">
            <User className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Profile Management</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage your personal information and notification preferences</p>
          </div>
        </div>
      </div>

      {/* Overview */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Info className="h-6 w-6 mr-2 text-brandColor" />
          Overview
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            The User Profile page allows you to manage your personal information and control how you receive
            notifications within the CRM system. You can:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
            <li>Update your display name</li>
            <li>View your email address and phone number (read-only)</li>
            <li>Check your assigned role and permissions</li>
            <li>Configure notification preferences for leads</li>
            <li>Pause all notifications temporarily</li>
          </ul>
        </div>
      </section>

      {/* Accessing Profile */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <User className="h-6 w-6 mr-2 text-brandColor" />
          Accessing Your Profile
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="bg-green-100 dark:bg-blue-900/20 rounded-full p-2 mt-1">
                <CheckCircle className="h-5 w-5 text-brandColor" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Step 1: Open Settings</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Click on your profile icon in the top right corner of the navigation bar and select "Settings"
                  from the dropdown menu.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-green-100 dark:bg-blue-900/20 rounded-full p-2 mt-1">
                <CheckCircle className="h-5 w-5 text-brandColor" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Step 2: Navigate to Profile</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  In the settings sidebar, click on "Profile" to access your user profile management page.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Profile Information Tab */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <User className="h-6 w-6 mr-2 text-brandColor" />
          Profile Information Tab
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            The Profile Information tab displays and allows editing of your basic account details:
          </p>

          <div className="space-y-6">
            {/* User Name */}
            <div className="border-l-4 border-blue-500 bg-green-50 dark:bg-blue-900/10 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <User className="h-5 w-5 text-brandColor mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Display Name</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                    Your full name as it appears throughout the CRM system.
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded p-2 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Editable:</span>
                    <span className="ml-2 text-green-600 font-medium">Yes</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Address */}
            <div className="border-l-4 border-gray-400 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center">
                    Email Address
                    <Lock className="h-3 w-3 ml-2 text-gray-400" />
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                    Your email address used for login and system communications.
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded p-2 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Editable:</span>
                    <span className="ml-2 text-red-600 font-medium">No (Read-only)</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mt-2 italic">
                    Email addresses cannot be changed for security reasons. Contact your administrator if you
                    need to update your email.
                  </p>
                </div>
              </div>
            </div>

            {/* Phone Number */}
            <div className="border-l-4 border-gray-400 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center">
                    Phone Number
                    <Lock className="h-3 w-3 ml-2 text-gray-400" />
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                    Your phone number associated with this account.
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded p-2 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Editable:</span>
                    <span className="ml-2 text-red-600 font-medium">No (Disabled)</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mt-2 italic">
                    Phone numbers are linked to your account authentication and cannot be modified here.
                  </p>
                </div>
              </div>
            </div>

            {/* Role */}
            <div className="border-l-4 border-gray-400 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center">
                    Role
                    <Lock className="h-3 w-3 ml-2 text-gray-400" />
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                    Your assigned role determines your permissions and access levels within the system.
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded p-2 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Editable:</span>
                    <span className="ml-2 text-red-600 font-medium">No (Read-only)</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mt-2 italic">
                    Only administrators can change user roles. See the Roles & Permissions documentation for
                    more information.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-green-50 dark:bg-blue-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Save className="h-5 w-5 text-brandColor mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <p className="font-medium text-green-900 dark:text-green-300 mb-1">Saving Changes</p>
                <p>
                  After updating your display name, click the "Save Changes" button at the bottom of the form.
                  The button will only be enabled if you've made changes to editable fields.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notification Settings Tab */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Bell className="h-6 w-6 mr-2 text-brandColor" />
          Notification Settings Tab
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            The Notification Settings tab allows you to control how you receive notifications about new leads
            and system events:
          </p>

          <div className="space-y-6">
            {/* Lead Notifications */}
            <div className="border-l-4 border-green-500 bg-green-50 dark:bg-green-900/10 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Bell className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Lead Notifications</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                    Choose how you want to be notified when new leads are assigned to you or when lead activity occurs:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <Mail className="h-5 w-5 text-brandColor mt-0.5" />
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">Email Notifications</h5>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">
                          Receive notifications via email when new leads are assigned or updated. Emails are
                          sent to your registered email address.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">WhatsApp Notifications</h5>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">
                          Get instant notifications via WhatsApp for time-sensitive lead updates and assignments.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-green-50 dark:bg-blue-900/10 rounded p-3 text-sm">
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Tip:</span> You can enable multiple notification channels
                      simultaneously to ensure you never miss important lead updates.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pause Notifications */}
            <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/10 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Pause className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Pause All Notifications</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                    Temporarily pause all notifications when you're on vacation, in a meeting, or need
                    uninterrupted focus time.
                  </p>

                  <div className="space-y-3">
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">No, keep notifications active</span>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        You'll receive notifications based on your selected preferences above (Email, WhatsApp).
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">Yes, pause all notifications</span>
                        <Pause className="h-5 w-5 text-amber-600" />
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        Temporarily disable all notifications regardless of your channel preferences. You can
                        re-enable them anytime.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Status */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <Info className="h-5 w-5 mr-2 text-brandColor" />
                Current Notification Status
              </h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
                At the bottom of the Notification Settings tab, you'll see your current notification status:
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded p-3">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-900 dark:text-green-300">
                      Notifications Active
                    </span>
                  </div>
                  <p className="text-xs text-green-800 dark:text-green-400 mt-1">
                    You're receiving notifications based on your preferences
                  </p>
                </div>
                <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded p-3">
                  <div className="flex items-center">
                    <Pause className="h-5 w-5 text-yellow-600 mr-2" />
                    <span className="text-sm font-medium text-yellow-900 dark:text-yellow-300">
                      Notifications Paused
                    </span>
                  </div>
                  <p className="text-xs text-yellow-800 dark:text-yellow-400 mt-1">
                    All notifications are temporarily disabled
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Best Practices</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-green-100 dark:bg-blue-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Bell className="h-6 w-6 text-brandColor" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Enable Multiple Channels</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              For critical lead notifications, enable both Email and WhatsApp to ensure you receive alerts even
              if you're away from your email inbox.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-amber-100 dark:bg-amber-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Pause className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Use Pause During Off-Hours</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Pause notifications during evenings, weekends, or vacations to maintain work-life balance. Remember
              to re-enable them when you return.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-green-100 dark:bg-green-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Keep Profile Updated</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Regularly update your display name if it changes. This helps team members identify you correctly
              in lead assignments and communications.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Save className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Save Changes Regularly</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Don't forget to click "Save Changes" after updating your preferences. Unsaved changes will be lost
              if you navigate away from the page.
            </p>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <AlertCircle className="h-6 w-6 mr-2 text-amber-600" />
          Important Notes
        </h2>
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
          <ul className="space-y-3">
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Read-Only Fields:</span> Email address and phone number cannot be
                changed through the profile page. Contact your system administrator if these need to be updated.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">WhatsApp Requirements:</span> WhatsApp notifications require your
                phone number to be verified and connected to WhatsApp. Contact support if you're not receiving
                WhatsApp notifications.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Paused Notifications:</span> When notifications are paused, you'll
                still see in-app notifications and alerts when you log into the CRM, but external notifications
                (Email, WhatsApp) will be suspended.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Role Permissions:</span> Your role determines what features and
                data you can access. If you need additional permissions, contact your administrator to discuss
                a role change.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Troubleshooting</h2>
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
              Not Receiving Email Notifications
            </h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm ml-6">
              <li>Check your spam/junk folder</li>
              <li>Verify that Email notifications are enabled in your settings</li>
              <li>Ensure notifications are not paused</li>
              <li>Confirm your email address is correct (check with administrator)</li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
              Changes Not Saving
            </h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm ml-6">
              <li>Ensure you clicked the "Save Changes" button</li>
              <li>Check for error messages or validation warnings</li>
              <li>Try refreshing the page and making changes again</li>
              <li>Clear your browser cache if the issue persists</li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
              WhatsApp Notifications Not Working
            </h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm ml-6">
              <li>Verify your phone number is registered with WhatsApp</li>
              <li>Check if WhatsApp integration is enabled for your account</li>
              <li>Ensure WhatsApp notifications are enabled in your settings</li>
              <li>Contact support to verify WhatsApp integration status</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Need Help */}
      <section className="bg-gradient-to-r from-green-50 to-purple-50 dark:from-green-900/10 dark:to-purple-900/10 rounded-lg p-6 border border-green-200 dark:border-green-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
          <Info className="h-5 w-5 mr-2 text-brandColor" />
          Need Help?
        </h3>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          If you're experiencing issues with your profile settings or need to update read-only fields like your
          email address or phone number, please contact your system administrator or support team.
        </p>
        <a
          href="mailto:support@yourcrm.com"
          className="inline-flex items-center text-brandColor dark:text-green-400 hover:text-brandColor dark:hover:text-green-300 font-medium"
        >
          <Mail className="h-4 w-4 mr-2" />
          Contact Support
        </a>
      </section>
    </div>
  );
};

export default UserProfileDocs;
